<script setup>
import { useHead } from "@vueuse/head";
import RealtimeKujibikiCta from "@/components/molecules/RealtimeKujibikiCta.vue";

// SEO構造化データ（Article スキーマ）
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline:
    "VTuber向けグッズ販売の完全ガイド｜作り方・費用・売れる仕組み・売り方まで徹底解説（2025年版）",
  description:
    "グッズ販売を始めたいVTuber様のための完全ガイド。作り方・費用・売れる仕組みをわかりやすく解説。",
  datePublished: "2025-11-13",
  dateModified: "2025-11-13",
  author: {
    "@type": "Organization",
    name: "SukiSukiらいぶすてーしょん",
  },
  publisher: {
    "@type": "Organization",
    name: "SukiSukiらいぶすてーしょん",
  },
  image: "/img/airticlepic/articlerealtime2.webp",
};

// メタ情報と構造化データを設定
useHead({
  title:
    "VTuber向けグッズ販売の完全ガイド｜作り方・費用・売れる仕組み・売り方まで徹底解説（2025年版）| SukiSukiらいぶすてーしょん",
  meta: [
    {
      name: "description",
      content:
        "グッズ販売を始めたいVTuber様のための完全ガイド。作り方・費用・売れる仕組みをわかりやすく解説。",
    },
    {
      property: "og:title",
      content:
        "VTuber向けグッズ販売の完全ガイド｜作り方・費用・売れる仕組み・売り方まで徹底解説（2025年版） | SukiSukiらいぶすてーしょん",
    },
    {
      property: "og:description",
      content:
        "グッズ販売を始めたいVTuber様のための完全ガイド。作り方・費用・売れる仕組みをわかりやすく解説。",
    },
    {
      property: "og:type",
      content: "website",
    },
    {
      property: "og:image",
      content: "https://sukisuki.live/img/airticlepic/articlerealtime2.webp",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify(articleSchema),
    },
  ],
});
</script>

<template>
  <v-app>
    <!-- ヒーローセクション -->
    <figure class="hero-section">
      <img
        src="/img/airticlepic/articlerealtime2.webp"
        alt="記事のタイトルを解説するヒーロー画像"
      />
    </figure>

    <!-- パンくずナビゲーション -->
    <nav aria-label="パンくずリスト" class="breadcrumb-section">
      <v-container>
        <ol class="breadcrumb">
          <li><a href="/academy">TOP</a></li>
          <li>リアルタイムくじ引き</li>
        </ol>
      </v-container>
    </nav>

    <!-- メインコンテンツ -->
    <main>
      <article class="article-content">
        <v-container>
          <!-- 記事タイトル -->
          <div class="article-title-box">
            <h1>
              VTuber向けグッズ販売の完全ガイド｜作り方・費用・売れる仕組み・売り方まで徹底解説（2025年版）
            </h1>
          </div>

          <!-- 更新日 -->
          <div class="update-date-box">
            <time datetime="2025-11-13">更新日:2025年11月13日</time>
          </div>

          <!-- イントロダクション -->
          <div class="intro-box">
            <p>
              グッズ販売を始めたいVTuber様のための完全ガイド。作り方・費用・売れる仕組みをわかりやすく解説。
            </p>
          </div>

          <section class="learn-section">
            <h2 class="learn-title">この記事でわかる内容</h2>
            <div class="learn-content">
              <p>
                VTuber活動では「ファンが形として持てるもの」への需要が高く、
                <span class="bold-text">グッズ販売は活動の幅を広げる上で欠かせない要素</span>です。
              </p>
              <p>ただし…</p>
              <p>・どんなグッズを作ればいいのか</p>
              <p>・費用やリスクはどの程度なのか</p>
              <p>そもそもどうやって売ればいいのか</p>
              <p>という疑問を持つVTuberさんも多いはず。</p>
              <p>
                この記事では、<span class="bold-text"
                  >VTuberがグッズを作って販売するまでの流れを一から解説</span
                >し、さらに2025年に注目されている<span class="bold-text"
                  >配信と連動した“リアルタイムくじ引き”という新しい販売方式</span
                >についても紹介します。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">VTuberがグッズ販売をするメリット</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text">✔ファンの“所有欲”を満たせる</span>
              </p>
              <p>推し活文化では、缶バッジ・アクスタなど「形に残る応援」が好まれます。</p>
              <p>
                <span class="bold-text">✔雑談・お知らせなど配信企画と相性が良い</span>
              </p>
              <p>新グッズの告知は、それだけで配信の話題になります。</p>
              <p><span class="bold-text">✔世界観を広げやすい</span></p>
              <p>グッズは魅力を視覚的に伝える最強のツール。</p>
            </div>
          </section>

          <!-- CTA ボタン -->
          <RealtimeKujibikiCta article-id="academy-article-realtime-2" />

          <section class="learn-section">
            <h2 class="learn-title">売れやすいグッズの種類</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text"
                  >VTuber界隈は 500〜3,000円台のアイテムが特に売れやすい傾向。</span
                >
              </p>
              <p>・缶バッジ（300〜500円）</p>
              <p>・アクリルキーホルダー（1,000円〜2,000円）</p>
              <p>・アクリルスタンド（1,500〜2,000円）</p>
              <p>・アクリルブロック（3,000〜4,500円）</p>
              <p>・タペストリー（1,500〜2,500円）</p>
              <p>コレクション性、手に取りやすい価格、普段使いしやすさが大事です。</p>
            </div>
          </section>

          <section class="learn-section">
            <h2 class="learn-title">グッズ制作の流れ</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text">① コンセプト決め</span>
              </p>
              <p>誕生日、記念日、衣装デザインなど。</p>
              <p><span class="bold-text">② イラスト・デザイン制作</span></p>
              <p>自作または外注（1万円〜数万円）。</p>
              <p><span class="bold-text">③ 制作会社を選ぶ</span></p>
              <p>pixivFACTORY、original-factory、BOOTH工場など多数。</p>
              <p><span class="bold-text">④ 発注（ロットを決定）</span></p>
              <p><span class="bold-text">⑤ 販売ページを作成</span></p>
            </div>
          </section>

          <section class="learn-section">
            <h2 class="learn-title">グッズ制作にかかる費用（目安）</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text">◎ アクスタ</span>
              </p>
              <p>・制作：600〜2,000円</p>
              <p>・デザイン：10,000円〜</p>
              <p><span class="bold-text">◎ 缶バッジ</span></p>
              <p>・制作：150〜300円</p>
              <p>・デザイン：5,000円〜</p>
            </div>
          </section>

          <section class="learn-section">
            <h2 class="learn-title">在庫リスクと販売方式</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text">① 受注生産（ノーリスク）</span>
              </p>
              <p>注文が入ってから制作。個人勢はこれが最も簡単。</p>
              <p><span class="bold-text">② 小ロット生産（ミニマムリスク）</span></p>
              <p>少量から在庫を持つ方式。</p>
              <p><span class="bold-text">③ 在庫を作って販売（中規模以上向け）</span></p>
              <p>ライブイベントやコミケ参加などに相性が良い。</p>
            </div>
          </section>

          <section class="learn-section">
            <h2 class="learn-title">売れるグッズの仕組み｜ファン心理と購入の瞬間</h2>
            <div class="learn-content">
              <p><span class="bold-text">ファンが購入しやすいタイミングとは？</span></p>
              <p>・生誕祭、誕生日、周年</p>
              <p>・新衣装お披露目</p>
              <p>・配信の盛り上がりの瞬間</p>
              <p>・ファン参加型企画中</p>
              <p>
                特に重要なのは、<span class="bold-text"
                  >“テンションが上がっている瞬間に販売が重なる”</span
                >こと。
              </p>
              <p>ここが2025年のグッズ販売の主流になりつつあります。</p>
            </div>
          </section>

          <!-- CTA ボタン -->
          <RealtimeKujibikiCta article-id="academy-article-realtime-2" />

          <section class="learn-section">
            <h2 class="learn-title">【最新】配信と連動する“リアルタイム販売”が台頭</h2>
            <div class="learn-content">
              <p>
                2024〜2025年は、
                <span class="bold-text">「配信中にグッズを買える仕組み」</span>が急増しています。
              </p>
              <p>理由はシンプルで、</p>
              <p><span class="bold-text">✔ 熱量が高い瞬間に買われる</span></p>
              <p><span class="bold-text">✔ 配信が企画として盛り上がる</span></p>
              <p><span class="bold-text">✔ ファン参加型で楽しさが生まれる</span></p>
              <p><span class="bold-text">✔ 報告ツイートやクリップが自然に増える</span></p>
              <p>
                SNS経由の静的な告知だけより、
                <span class="bold-text">買われる確率が圧倒的に高い</span>という特徴があります。
              </p>
            </div>
          </section>

          <section class="learn-section">
            <h2 class="learn-title">配信を盛り上げる新方式「リアルタイムくじ引き」とは？</h2>
            <div class="learn-content">
              <p>「くじ引き」と「配信の盛り上がり」を組み合わせた最新の販売方式。</p>
              <p><span class="bold-text">✔ 内容</span></p>
              <p>・視聴者がスマホで“くじ”を引く</p>
              <p>・当たりがその場でわかる(ハズレなし)</p>
              <p>・当たりが出た瞬間、配信も盛り上がる</p>
              <p>・ファンの皆様にその場でお礼が言える</p>
              <p>・景品は缶バッジ・アクスタ・限定カードなど自由に設定OK</p>
              <p><span class="bold-text">✔ 他の方式との違い</span></p>
              <div class="">
                <table class="comparison-table">
                  <thead>
                    <tr>
                      <th>比較項目</th>
                      <th>従来のオンラインくじ</th>
                      <th>リアルタイムくじ引き</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>抽選のタイミング</td>
                      <td>購入後すぐ</td>
                      <td>配信中・リアルタイム</td>
                    </tr>
                    <tr>
                      <td>結果の共有</td>
                      <td>個人の画面で完結</td>
                      <td>配信内で共有・発表</td>
                    </tr>
                    <tr>
                      <td>主な目的</td>
                      <td>グッズの購入</td>
                      <td>グッズの購入&ファンと盛り上がる</td>
                    </tr>
                    <tr>
                      <td>感情体験</td>
                      <td>一人で結果を見る</td>
                      <td>全員で盛り上がる＆反応が見れる</td>
                    </tr>
                    <tr>
                      <td>拡散性</td>
                      <td>低い</td>
                      <td>高い（SNS・切り抜きなど）</td>
                    </tr>
                  </tbody>
                </table>
                <p>✔ 特におすすめの人</p>
                <p>・個人勢</p>
                <p>・企画ネタが欲しい人</p>
                <p>・ファンと双方向で盛り上がりたい人</p>
                <p>・グッズ販売をもっと盛り上げたい人</p>
              </div>
            </div>
          </section>

          <section class="learn-section">
            <h2 class="learn-title">まとめ｜グッズ販売は「売り方」で大きく変わる</h2>
            <div class="learn-content">
              <p>
                2025年のVTuber界隈では、
                <span class="bold-text"
                  >“どんなグッズを作るか”より “どう売るか” が結果を左右します。</span
                >
              </p>
              <p>・販売タイミング</p>
              <p>・企画性</p>
              <p>・ファン参加性</p>
              <p>・配信との相性</p>
              <p>これらを意識すると、グッズ販売は一気に伸びます。</p>
              <p>
                そして今最も注目されているのが
                <span class="bold-text">「リアルタイムくじ引き」</span>という新しい販売方式です。
              </p>
            </div>
          </section>

          <section class="learn-section">
            <h2 class="learn-title">興味のあるVTuberさんへ</h2>
            <div class="learn-content">
              <p>・「素材のみ」いただくだけで、グッズのデザイン・製造・発送まで代行</p>
              <p>・初期費用ゼロ（グッズのデザイン費用もかかりません）</p>
              <p>・個人勢も多数利用中</p>
              <p>
                <span class="bold-text">リアルタイムくじ引き企画の無料説明</span>をご希望の方は、
                30分ほどで概要をご案内できます。
              </p>
              <p>「ちょっと気になる」という方も大歓迎です。</p>
            </div>
          </section>

          <!-- CTA ボタン -->
          <RealtimeKujibikiCta article-id="academy-article-realtime-2" class="mb-10" />
        </v-container>
      </article>
    </main>
  </v-app>
</template>

<style scoped>
/* ヒーローセクション */
.hero-section {
  padding: 20px;
  width: 100%;
}

.hero-section img {
  width: 100%;
  height: auto;
  display: block;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-main-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
}

.hero-left-content {
  flex: 1;
  text-align: left;
}

.hero-tag {
  background-color: #c8007f;
  color: white;
  padding: 8px 20px;
  border-radius: 4px;
  display: inline-block;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 20px;
}

.hero-title {
  font-size: 2.5rem;
  color: #6b4c93;
  font-weight: bold;
  margin: 0 0 10px 0;
}

.hero-subtitle {
  font-size: 1.2rem;
  color: #6b4c93;
  margin: 0;
}

.hero-right-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-image {
  max-width: 100%;
  height: auto;
}

/* パンくず */
.breadcrumb-section {
  background-color: white;
  /* padding: 15px 0; */
}

.breadcrumb {
  color: #666;
  font-size: 14px;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 10px;
  padding-bottom: 8px;
  text-align: left;
  display: flex;
  list-style: none;
  margin: 0;
  gap: 8px;
}

.breadcrumb li {
  display: inline-block;
}

.breadcrumb li:not(:last-child)::after {
  content: ">";
  margin-left: 8px;
}

.breadcrumb a {
  color: #666;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

/* メインコンテンツ */
::v-deep(.v-container) {
  text-align: left;
  padding: 0 !important;
}

.article-content {
  /* margin: 40px 0; */
  text-align: left;
}

.article-content h2,
.article-content h3 {
  text-align: left;
}

.article-title-box {
  margin-bottom: 5px;
}

.article-title-box h1,
.article-title-box h2 {
  color: #333;
  font-size: 18px;
  margin: 0;
  font-weight: bold;
  text-align: left;
  padding-left: 20px;
  padding-right: 20px;
  line-height: 1.6;
}

.update-date-box {
  padding: 10px 20px;
  margin-bottom: 10px;
}

.update-date-box p,
.update-date-box time {
  color: #333;
  margin: 0;
  font-size: 14px;
  text-align: left;
}

.intro-box {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  margin-left: 20px;
  margin-right: 20px;
  font-size: 14px;
}

.intro-box p {
  color: #333;
  margin: 0;
  line-height: 1.6;
  text-align: left;
}

.learn-section {
  margin: 30px 0;
}

.learn-title {
  color: #333;
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 20px 20px;
  text-align: left;
  background-color: #ffe0e0;
  padding: 15px 0 15px 20px;
  position: relative;
  line-height: 1.6;
}

.learn-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 8px;
  height: 100%;
  background-color: #982da6;
}

.learn-content {
  margin-left: 20px;
  text-align: left;
}

.learn-content p {
  color: #333;
  line-height: 1.6;
  margin: 0 20px 15px 0;
  text-align: left;
  font-size: 16px;
}

.custom-content {
  line-height: 1.6;
}

.bold-text {
  font-weight: bold;
}

/* 比較テーブル */
.comparison-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  font-size: 14px;
}

.comparison-table th,
.comparison-table td {
  padding: 12px;
  text-align: left;
  border: 1px solid #ddd;
}

.comparison-table th {
  background-color: #ffe0e0;
  font-weight: bold;
  color: #333;
}

.comparison-table td {
  background-color: #fff;
}

.comparison-table tr:hover {
  background-color: #f9f9f9;
}

.learn-content-table {
  margin-left: 20px;
  margin-right: 20px;
  text-align: left;
  line-height: 1.6;
}

@media (min-width: 750px) {
  .hero-section {
    padding: 0 25%;
  }

  .hero-section img {
    width: 100%;
    height: auto;
    display: block;
  }
  .breadcrumb {
    padding: 10px 25%;
  }
  .v-container {
    max-width: 5000px !important;
  }
  .article-title-box {
    padding: 0 25%;
  }
  .article-title-box h1,
  .article-title-box h2 {
    font-size: 20px;
    padding: 0;
    line-height: 1.6;
  }
  .update-date-box {
    padding: 10px 25%;
  }
  .update-date-box p,
  .update-date-box time {
    padding: 0;
  }
  .intro-box {
    padding: 10px;
  }
  .intro-box p {
    font-size: 16px;
    line-height: 1.6;
  }
  .intro-box {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-title {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-content {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-content p {
    margin-left: 0;
    margin-right: 0;
  }
  .learn-content-table {
    margin-left: 25%;
    margin-right: 25%;
    text-align: left;
  }
}
</style>
